import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Trash2, CheckCircle2, XCircle } from "lucide-react";

const roles = ["retail", "member", "reseller", "agen", "mitra", "admin"];
const roleLabels = { retail: "Retail", member: "Member", reseller: "Reseller", agen: "Agen", mitra: "Mitra", produsen: "Mitra", admin: "Admin" };
const roleColors = {
  admin: "bg-red-50 text-red-700",
  member: "bg-emerald-50 text-emerald-700",
  reseller: "bg-blue-50 text-blue-700",
  agen: "bg-amber-50 text-amber-700",
  mitra: "bg-purple-50 text-purple-700",
  produsen: "bg-purple-50 text-purple-700",
  retail: "bg-stone-100 text-stone-600",
};


const getDeleteMessage = (response, fallback) => {
  const data = response?.data || {};
  if (data.action === "soft_deleted") return data.message || fallback;
  return data.message || fallback;
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, locale } = useLanguage();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchUsers = () => API.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = users.filter((u) => {
    const term = searchQuery.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.phone || "").toLowerCase().includes(term) ||
      (u.membership_requested_role || "").toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const updateRole = async (userId, role) => {
    try {
      await API.put(`/admin/users/${userId}/role`, { role });
      toast.success(t("common.saved") || "Role updated!");
      fetchUsers();
    } catch { toast.error("Failed to update"); }
  };

  const reviewMembership = async (user, action) => {
    const label = action === "approve" ? "approve" : "reject";
    if (!window.confirm(`Yakin ${label} pengajuan ${roleLabels[user.membership_requested_role] || user.membership_requested_role} untuk ${user.name || user.email}?`)) return;
    try {
      await API.put(`/admin/users/${user.user_id}/membership-application/${user.membership_application_id}`, { action });
      toast.success(action === "approve" ? "Pengajuan disetujui" : "Pengajuan ditolak");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses pengajuan");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t("admin_users.confirm_delete") || "Are you sure?")) return;
    try {
      const response = await API.delete(`/admin/users/${userId}`);
      toast.success(getDeleteMessage(response, t("admin_users.deleted") || "User deleted!"));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <AdminLayout title={t("admin_nav.users")}>
      <div data-testid="admin-users">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <p className="text-sm text-stone-500 whitespace-nowrap">{filteredUsers.length} {t("admin_nav.users").toLowerCase()}</p>
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-stone-400" />
            </div>
            <Input
              type="text"
              placeholder={t("admin_users.search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>

        <div className="bg-white border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_users.user")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">WhatsApp</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">Membership</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_users.role")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_users.actions")}</th>
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-stone-400">{t("common.loading")}</td></tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-stone-400">{t("admin_users.no_users")}</td></tr>
                ) : paginatedUsers.map((u) => (
                  <tr key={u.user_id} className="border-b border-stone-50" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3 font-medium text-stone-800">{u.name}</td>
                    <td className="p-3 text-stone-600">{u.email}</td>
                    <td className="p-3 text-stone-600">{u.phone || u.membership_application_phone || "-"}</td>
                    <td className="p-3">
                      {u.membership_application_status === "pending" ? (
                        <div className="space-y-2 min-w-[180px]">
                          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                            Pending {roleLabels[u.membership_requested_role] || u.membership_requested_role}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => reviewMembership(u, "approve")}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button
                              onClick={() => reviewMembership(u, "reject")}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-red-200 text-red-600 text-xs hover:bg-red-50"
                            >
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                          </div>
                        </div>
                      ) : u.membership_application_status ? (
                        <Badge className={`${u.membership_application_status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} hover:bg-opacity-80 text-[10px] uppercase tracking-wider font-semibold`}>
                          {u.membership_application_status} {roleLabels[u.membership_requested_role] || u.membership_requested_role || ""}
                        </Badge>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={`${roleColors[u.role] || ""} hover:bg-opacity-80 text-[10px] uppercase tracking-wider font-semibold`}>{roleLabels[u.role] || u.role}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={`${u.status === "inactive" ? "bg-stone-100 text-stone-600" : "bg-emerald-50 text-emerald-700"} hover:bg-opacity-80 text-[10px] uppercase tracking-wider font-semibold`}>
                        {u.status === "inactive" ? "Inactive" : "Active"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)}>
                          <SelectTrigger className="w-[110px] h-8 text-xs px-2"><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map((r) => <SelectItem key={r} value={r} className="text-xs">{roleLabels[r] || r}</SelectItem>)}</SelectContent>
                        </Select>
                        <button 
                          onClick={() => handleDelete(u.user_id)}
                          className="p-1.5 text-stone-400 hover:text-red-500 transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-4 mt-6">
            <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
